import { useState } from "react";
import { Mail, KeyRound, ShieldCheck, ShieldOff, Eye, EyeOff, Loader2, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AllowedTemplatesSection } from "@/components/employee/AllowedTemplatesSection";

function genPwd() {
  const a = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const b = "abcdefghjkmnpqrstuvwxyz23456789";
  const all = a + b;
  let out = "";
  for (let i = 0; i < 10; i++) {
    out += all[Math.floor(Math.random() * all.length)];
  }
  return out;
}

export function PortalAccessTab({ employee, onChanged }) {
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(true);
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const hasEmail = !!employee?.email;
  const hasAccess = !!employee?.portal_password_hash;

  const setPwd = async () => {
    if (!password || password.length < 6) {
      toast.error("A jelszónak min. 6 karakternek kell lennie.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/employees/${employee._id}/portal-password`, { password });
      toast.success("Portál hozzáférés beállítva.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Sikertelen.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Biztosan megszünteted a portál hozzáférést?")) return;
    setRevoking(true);
    try {
      await api.delete(`/employees/${employee._id}/portal-password`);
      toast.success("Hozzáférés visszavonva.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Sikertelen.");
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="portal-access-tab">
      {/* Status banner */}
      <div
        className={
          "flex items-center gap-3 rounded-2xl border p-4 " +
          (hasAccess
            ? "border-emerald-200/70 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10"
            : "border-slate-200 bg-slate-50/60 dark:border-border dark:bg-foreground/[0.02]")
        }
      >
        <div
          className={
            "grid h-10 w-10 place-items-center rounded-xl " +
            (hasAccess
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200"
              : "bg-slate-100 text-slate-500 dark:bg-foreground/[0.06]")
          }
        >
          {hasAccess ? (
            <ShieldCheck className="h-5 w-5" strokeWidth={1.8} />
          ) : (
            <ShieldOff className="h-5 w-5" strokeWidth={1.8} />
          )}
        </div>
        <div className="flex-1">
          <p className="text-[13px] font-semibold tracking-tight">
            {hasAccess ? "Portál hozzáférés aktív" : "Nincs portál hozzáférés"}
          </p>
          <p className="text-[11.5px] text-muted-foreground">
            {hasAccess
              ? "A dolgozó a /dolgozo címen tud belépni az e-mail címével."
              : "Állíts be jelszót a dolgozónak a portálhoz."}
          </p>
        </div>
        {hasAccess && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-500/15"
            onClick={revoke}
            disabled={revoking}
            data-testid="revoke-portal-access"
          >
            {revoking ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Megszüntetés
          </Button>
        )}
      </div>

      {/* Email check */}
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center gap-2.5">
          <Mail className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Belépési e-mail
          </p>
        </div>
        {hasEmail ? (
          <p className="mt-2 font-mono text-[13.5px] tabular-nums">
            {employee.email}
          </p>
        ) : (
          <p className="mt-2 text-[12.5px] text-rose-600">
            Hiányzik az e-mail cím. Először add meg az „Adatok&#34; fülön.
          </p>
        )}
      </div>

      {/* Set password */}
      {hasEmail && (
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <KeyRound className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                {hasAccess ? "Jelszó visszaállítása" : "Jelszó beállítása"}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              onClick={() => setPassword(genPwd())}
              data-testid="generate-password"
            >
              Generálás
            </Button>
          </div>
          <div className="relative">
            <Input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 6 karakter"
              data-testid="portal-password-input"
              className="h-10 pr-20 font-mono tabular-nums"
              autoComplete="new-password"
            />
            <div className="absolute right-1 top-1 flex items-center gap-0.5">
              {password && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(password);
                    toast.success("Vágólapra másolva");
                  }}
                  className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                  aria-label="Másolás"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                aria-label={showPw ? "Elrejtés" : "Mutatás"}
              >
                {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10.5px] text-muted-foreground">
              A jelszót küldd el biztonságosan a dolgozónak. Jelszó hash-ként tároljuk.
            </p>
            <Button
              type="button"
              onClick={setPwd}
              disabled={busy || password.length < 6}
              data-testid="save-portal-password"
              className="shrink-0"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Mentés"}
            </Button>
          </div>
        </div>
      )}

      {hasAccess && employee?._id && (
        <AllowedTemplatesSection employee={employee} />
      )}
    </div>
  );
}
